import * as React from 'react';
import { createContext, useContext } from 'react';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';
import Flex from 'common/ui/Flex';
import { forwardRefNoClassName } from 'common/uiV3/common';
import isServer from 'common/util/isServer';

const avatarVariants = tv({
  slots: {
    base: ['box-border relative flex size-8 shrink-0'],
    image: ['box-border aspect-square size-full overflow-hidden'],
    fallback: ['box-border bg-muted flex size-full items-center justify-center'],
  },
  variants: {
    shape: {
      circle: {
        image: 'rounded-full',
        fallback: 'rounded-full',
      },
      square: {
        image: 'rounded-md',
        fallback: 'rounded-md',
      },
    },
  },
  defaultVariants: {
    size: 'default',
    shape: 'circle',
  },
});

const AvatarContext = createContext<VariantProps<typeof avatarVariants> | null>(null);

const useAvatarContext = () => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('AvatarContext not found');
  }
  return context;
};

/**
 * SSR-safe Avatar component that renders a simple div on the server
 * and hydrates to the full Radix UI component on the client
 */
const AvatarRoot = forwardRefNoClassName<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
    VariantProps<typeof avatarVariants> & { size?: number | `${string}px` }
>(({ size, shape, style, ...rest }, ref) => {
  const styles = avatarVariants({ shape });

  if (isServer()) {
    return (
      <AvatarContext.Provider value={{ shape }}>
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          data-slot="avatar"
          className={styles.base()}
          {...rest}
        />
      </AvatarContext.Provider>
    );
  }

  return (
    <AvatarContext.Provider value={{ shape }}>
      <AvatarPrimitive.Root
        ref={ref}
        data-slot="avatar"
        className={styles.base()}
        style={{ width: size, height: size, ...style }}
        {...rest}
      />
    </AvatarContext.Provider>
  );
});
AvatarRoot.displayName = 'Avatar';

const AvatarImage = forwardRefNoClassName<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>((props, ref) => {
  const styles = avatarVariants(useAvatarContext());

  if (isServer()) {
    return (
      // eslint-disable-next-line
      <img
        ref={ref as React.Ref<HTMLImageElement>}
        data-slot="avatar-image"
        className={styles.image()}
        {...props}
      />
    );
  }

  return (
    <AvatarPrimitive.Image
      ref={ref}
      data-slot="avatar-image"
      className={styles.image()}
      {...props}
    />
  );
});
AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = forwardRefNoClassName<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>((props, ref) => {
  const styles = avatarVariants(useAvatarContext());

  // On server or initial render, use a simple div
  if (isServer()) {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        data-slot="avatar-fallback"
        className={styles.fallback()}
        {...props}
      />
    );
  }

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      data-slot="avatar-fallback"
      className={styles.fallback()}
      {...props}
    />
  );
});
AvatarFallback.displayName = 'AvatarFallback';

const AvatarGroup = forwardRefNoClassName<
  React.ElementRef<typeof Flex>,
  React.ComponentPropsWithoutRef<typeof Flex>
>((props, ref) => {
  return (
    <Flex
      ref={ref}
      data-slot="avatar-group"
      className={cn(
        'box-border flex -space-x-2',
        '**:data-[slot=avatar-image]:border-2 **:data-[slot=avatar-image]:border-white',
        '**:data-[slot=avatar-fallback]:border-2 **:data-[slot=avatar-fallback]:border-white',
      )}
      {...props}
    />
  );
});
AvatarGroup.displayName = 'AvatarGroup';

const Avatar = Object.assign(AvatarRoot, {
  Image: AvatarImage,
  Fallback: AvatarFallback,
  Group: AvatarGroup,
});

export default Avatar;
